import { financeApi } from "@/features/finance/services/finance-api"
import type {
  AccountingJournalQuery,
  JournalEntryView,
  JournalResponse,
} from "@/features/finance/model/accounting-reports-types"

/**
 * The journal is paginated: the viewer shows ONE page, and for a long time the CSV export was built from that
 * same page — so "export the general journal" wrote a file containing the first 50 postings and called it the
 * journal. Nobody was told. Reconciling against that file is reconciling against a fiction.
 *
 * The export therefore fetches every page itself, and if it cannot finish, it fails loudly (see
 * `JournalExportResult`). A silent cap is the bug, not the fix.
 */

/** Page size the on-screen viewer fetches. The export is deliberately NOT bound by it. */
export const JOURNAL_VIEW_PAGE_SIZE = 50

/** The API caps `limit` at 200 — pull the largest legal page so a full export costs the fewest round-trips. */
export const JOURNAL_EXPORT_PAGE_SIZE = 200

/** Hard stop, so a pathological dataset cannot hang the browser forever. 250 × 200 = 50,000 entries. */
export const JOURNAL_EXPORT_MAX_PAGES = 250

/** How many journal entries a single export can cover. Past this the export refuses rather than truncates. */
export const JOURNAL_EXPORT_MAX_ENTRIES = JOURNAL_EXPORT_PAGE_SIZE * JOURNAL_EXPORT_MAX_PAGES

export type JournalExportResult =
  | { complete: true; entries: JournalEntryView[] }
  /** The hard stop was reached: the caller must SAY SO and write no file. `total` is what the server reports. */
  | { complete: false; total: number }

type JournalPageFetcher = (query: AccountingJournalQuery) => Promise<JournalResponse>

/**
 * Fetches every page of the journal for `query` (the report's current scope + period), in order.
 *
 * Termination is driven by the response's own pagination metadata (`total`), with a short/empty page as a
 * belt-and-braces stop so a wrong `total` cannot spin the loop.
 */
export async function fetchAllJournalEntries(
  query: AccountingJournalQuery,
  fetchPage: JournalPageFetcher = financeApi.getAccountingJournal,
): Promise<JournalExportResult> {
  const entries: JournalEntryView[] = []
  let total = 0

  for (let page = 1; page <= JOURNAL_EXPORT_MAX_PAGES; page += 1) {
    const response = await fetchPage({ ...query, page, limit: JOURNAL_EXPORT_PAGE_SIZE })
    total = response.total
    entries.push(...response.items)

    if (response.items.length === 0 || entries.length >= total) {
      return { complete: true, entries }
    }
  }

  return { complete: false, total }
}
